<template>
  <v-dialog
    v-model="active"
    width="70%"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">Upload CSV File</span>
      </v-card-title>
      <v-card-text>
        <v-file-input
          :key="componentKey"
          v-model="file"
          label="Choose CSV file"
          accept=".csv"
          dense
          outlined
          @change="handlePreview"
        />
        <v-alert
          v-if="messages"
          :type="messages_type || 'info'"
          dense
          text
        >
          {{ messages }}
        </v-alert>
        <v-row>
          <v-col
            cols="12"
            class="btn-download-template"
          >
            <v-btn
              text
              small
              color="primary"
              @click="downloadTemplate"
            >
              Download Template
            </v-btn>
          </v-col>
        </v-row>
        <v-data-table
          v-if="previewData.length > 0"
          :headers="filteredHeaders"
          :items="previewData"
          :expanded.sync="errorExpanded"
          :loading="loading"
          :hide-default-footer="true"
          :items-per-page="-1"
          fixed-header
          item-key="Name"
          class="elevation-1 mt-3"
          show-expand
          style="max-height: 300px; overflow-y: scroll;"
        >
          <template v-slot:item="{ item }">
            <tr :class="{ '': !item.valid }">
              <td>
                <v-icon
                  @click="toggleExpand(item)">
                  {{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </td>
              <td
                v-for="header in filteredHeaders"
                :key="header.value"
              >
                {{ item[header.value] }}
              </td>
            </tr>
          </template>

          <template v-slot:expanded-item="{ item }">
            <td
              v-if="item.errorMessage && item.errorMessage.length"
              :colspan="headers.length">
              <v-alert
                :type="messages_type"
                dense
                text
              >
                {{ item.errorMessage.join(', ') }}
              </v-alert>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="error"
          text
          @click="close">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="!canUpload"
          :class="{ 'upload-disabled': !canUpload }"
          :loading="loading"
          @click="handleUpload"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CelebProvider from '@/resources/CelebProvider'
import { ExportToCsv } from 'export-to-csv'
import { mapActions } from 'vuex/dist/vuex.common'

const CelebService = new CelebProvider()

export default {
  name: 'ImportCsvModalPreview',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    marketOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      active: this.value,
      file: null,
      previewData: [],
      errorExpanded: [],
      headers: [
        { text: 'Name', value: 'Name', width: '150px' },
        { text: 'Email', value: 'Email', width: '100px' },
        { text: 'Tel', value: 'Tel', width: '100px' },
        { text: 'Market', value: 'Market', width: '100px' },
        { text: 'Instagram', value: 'Instagram', width: '150px' },
        { text: 'Facebook', value: 'Facebook', width: '150px' },
        { text: 'Tiktok', value: 'Tiktok', width: '150px' },
        { text: 'X / Twitter', value: 'X / Twitter', width: '150px' },
        { text: 'Redbook or Xiaohongshu', value: 'Redbook or Xiaohongshu', width: '150px' },
        { text: 'Lemon8', value: 'Lemon8', width: '150px' },
        { text: 'Shipping FirstName', value: 'Shipping FirstName', width: '180px' },
        { text: 'Shipping LastName', value: 'Shipping LastName', width: '180px' },
        { text: 'Shipping Email', value: 'Shipping Email', width: '100px' },
        { text: 'Shipping Address 1', value: 'Shipping Address 1', width: '350px' },
        { text: 'Shipping Address 2', value: 'Shipping Address 2', width: '350px' },
        { text: 'Sub-District', value: 'Sub-District', width: '350px' },
        { text: 'District', value: 'District', width: '100px' },
        { text: 'State Province', value: 'State Province', width: '150px' },
        { text: 'Country', value: 'Country', width: '100px' },
        { text: 'Postal Code', value: 'Postal Code', width: '120px' },
        { text: 'Is Error', value: 'valid' }
      ],
      componentKey: 0,
      messages: '',
      messages_type: 'info',
      loading: false
    }
  },
  computed: {
    hasErrors () {
      return this.previewData.some((row) => this.hasError(row))
    },
    canUpload () {
      return !this.loading
        && this.previewData.length > 0
        && this.previewData.every((row) => row.valid)
    },
    filteredHeaders () {
      return this.headers.filter((h) => h.value !== 'valid')
    }
  },
  watch: {
    value (val) {
      this.active = val
    },
    active (val) {
      this.$emit('input', val)
      if (!val) {
        this.resetForm()
      }
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    isEmpty (val) {
      return !val || val.trim() === ''
    },
    hasError (row) {
      return this.isInvalidRow(row)
    },
    toggleExpand (item) {
      this.errorExpanded = this.errorExpanded.includes(item)
        ? this.errorExpanded.filter((i) => i !== item)
        : [...this.errorExpanded, item]
    },
    isExpanded (item) {
      return this.errorExpanded.includes(item)
    },
    getError (row) {
      const messages = []

      const requiredFields = {
        'Name': 'Name is required',
        'Email': 'Email is required',
        'Tel': 'Tel is required',
        'Market': 'Market is required',
        'Shipping FirstName': 'Shipping FirstName is required',
        'Shipping LastName': 'Shipping LastName is required',
        'Shipping Address 1': 'Shipping Address 1 is required',
        'Sub-District': 'Sub-District is required',
        'District': 'District is required',
        'State Province': 'State Province is required',
        'Country': 'Country is required',
        'Postal Code': 'Postal Code is required'
      }

      for (const [field, errorMsg] of Object.entries(requiredFields)) {
        if (this.isEmpty(row[field])) {
          messages.push(errorMsg)
        }
      }

      if (row['Shipping Address 1'] && row['Shipping Address 1'].length > 45) {
        messages.push('Shipping Address 1 exceeds 45 characters')
      }

      if (row['Shipping Address 2'] && row['Shipping Address 2'].length > 45) {
        messages.push('Shipping Address 2 exceeds 45 characters')
      }

      if (row['Market'] && !this.marketOptions.includes(row['Market'])) {
        messages.push('Market is not valid')
      }

      return messages
    },
    isInvalidRow (row) {
      return this.getError(row).length > 0
    },
    isInvalidCell (row, field) {
      const requiredField = this.headers.find((h) => h.value === field)?.required
      return requiredField && (!row[field] || row[field].trim() === '')
    },
    close () {
      this.active = false
      this.resetForm()
    },
    downloadTemplate () {
      const headers = this.headers
        .filter((header) => header.value !== 'valid')
        .map((header) => header.text)

      const templateData = [Object.fromEntries(headers.map((header) => [header, '']))]

      const options = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        showTitle: false,
        filename: 'template',
        useTextFile: false,
        useBom: true,
        headers
      }

      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(templateData)
    },

    convertToCSV (data) {
      return data.map((row) => row.join(',')).join('\n')
    },

    handlePreview () {
      this.loading = true
      this.messages = null
      if (!this.file) {
        this.messages = 'No file selected. Please choose a CSV file.'
        this.messages_type = 'error'
        this.resetForm()
        this.loading = false
        return
      }
      this.readCSV(this.file)
      this.loading = false
    },
    readCSV (file) {
      if (!(file instanceof Blob)) {
        this.messages = 'Invalid file format. Please upload a CSV file.'
        this.messages_type = 'error'
        return
      }

      this.previewData = []
      const reader = new FileReader()
      reader.onload = (e) => {
        const arrayBuffer = e.target.result
        const encodings = ['utf-8']
        let decodedText = ''

        for (const encoding of encodings) {
          try {
            const decoder = new TextDecoder(encoding)
            decodedText = decoder.decode(arrayBuffer)
          } catch (error) {
            console.warn(`Failed to decode with ${encoding}, trying next...`)
          }
        }
        this.parseCSV(decodedText)
      }
      reader.readAsArrayBuffer(file)
    },
    parseCSV (csvData) {
      const processedData = csvData
        .split('\n')
        .map((row) => row.split(',')
            .map((origValue) => {
              const value = origValue.trim()
              return value.includes('"') ? value : `"${value}"`
            })
            .join(','))
        .join('\n')

      const rows = processedData.trim().split('\n')
      const headers = rows[0].split(',').map((header) => header.trim().replace(/^"|"$/g, ''))

      const requiredHeaders = this.headers.map((header) => header.text).filter((h) => h !== 'Is Error' && h !== '')
      const csvHeaders = headers.map((h) => h.trim())

      const missingColumns = requiredHeaders.filter((h) => !csvHeaders.includes(h))
      const extraColumns = csvHeaders.filter((h) => !requiredHeaders.includes(h))

      if (missingColumns.length > 0 || extraColumns.length > 0) {
        let errorMsg = 'Invalid CSV file.'
        if (missingColumns.length > 0) {
          errorMsg += ` Missing columns: ${missingColumns.join(', ')}.`
        }
        if (extraColumns.length > 0) {
          errorMsg += ` Extra columns: ${extraColumns.join(', ')}.`
        }

        this.messages = errorMsg
        this.messages_type = 'error'
        this.previewData = []
        return
      }

      this.previewData = rows
      .slice(1)
      .map((row) => this.splitCSVRow(row))
      .filter((cells) => cells.some((cell) => cell && cell.trim() !== ''))
      .map((cells) => {
        const obj = {}

        headers.forEach((header, index) => {
          if (header) {
            const formattedCell = cells[index]
              ? cells[index].replace(/^="|"$|="$/, '').trim()
              : ''

            obj[header] = formattedCell
          }
        })
        const errorMessage = this.getError(obj)

        return {
          ...obj,
          Country: obj['Country']
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
          valid: errorMessage.length === 0,
          errorMessage
        }
      })

      this.errorExpanded = this.previewData.filter((v) => {
        if (!v.valid) {
          this.messages = 'Some rows contain errors.'
          this.messages_type = 'error'
        }
        return !v.valid
      })

      if (this.previewData.length === 0) {
        this.messages = 'The uploaded file contains no data.'
        this.messages_type = 'error'
      }
    },

    splitCSVRow (row) {
      const regex = /(".*?"|[^",\n]+)(?=\s*,|\s*$)/g
      const cells = []
      let match = regex.exec(row)

      while (match !== null) {
        cells.push(match[1].replace(/^"|"$/g, ''))
        match = regex.exec(row)
      }

      return cells
    },

    async handleUpload () {
      if (!this.canUpload) {
        return
      }

      try {
        this.loading = true
        const payload = this.previewData.map((data) => {
          const address = {
            contactNo: data['Tel'],
            firstName: data['Shipping FirstName'],
            lastName: data['Shipping LastName'],
            address: data['Shipping Address 1'],
            address2: data['Shipping Address 2'],
            subDistrict: data['Sub-District'],
            district: data['District'],
            stateProvince: data['State Province'],
            country: data['Country'],
            postcode: data['Postal Code'],
            email: data['Shipping Email']
          }

          const socialMedias = [
            { platform: 'instagram', accountName: data['Instagram'] },
            { platform: 'facebook', accountName: data['Facebook'] },
            { platform: 'tiktok', accountName: data['Tiktok'] },
            { platform: 'lazada', accountName: data['Lazada'] },
            { platform: 'x/twitter', accountName: data['X / Twitter'] },
            { platform: 'redbook/xiaohongshu', accountName: data['Redbook or Xiaohongshu'] },
            { platform: 'lemon8', accountName: data['Lemon8'] }
          ].filter((socialMedia) => socialMedia.accountName)

          return {
            name: data['Name'],
            email: data['Email'],
            tel: data['Tel'],
            market: data['Market'],
            address,
            socialMedias
          }
        })

        const { data } = await CelebService.importCelebs(payload)

        if (data) {
          if (data.importedCount > 0) {
            this.setSnackbar({
              value: true,
              message: `Successfully imported ${data.importedCount} celebs.`,
              type: 'success'
            })
            if (data.duplicates && data.duplicates.length > 0) {
              this.setSnackbar({
                value: true,
                message: `Duplicate celebs that were not imported: ${data.duplicates.join(', ')}`,
                duration: 10000,
                type: 'warning'
              })
            }
            this.close()
            this.$emit('imported')
          } else if (data.importedCount === 0) {
            this.messages = 'No celebs were imported.'
            if (data.duplicates && data.duplicates.length > 0) {
              this.messages += `\nDuplicate entries detected: ${data.duplicates.join(', ')}`
            }
            this.messages_type = 'error'
          }
        }
        this.loading = false
      } catch (error) {
        this.messages = error.message
        this.messages_type = 'error'
        this.loading = false
      }
    },

    resetForm () {
      this.file = null
      this.messages_type = null
      this.previewData = []
      this.messages = ''
      this.componentKey++
    }
  }
}
</script>

<style scoped>
  .btn-download-template {
    display: flex;
    justify-content: flex-end;
  }
  .v-alert {
    bottom: 0px;
    margin: 0 auto;
  }
</style>
